/**
 * Orient tool — workspace orientation and session bootstrapping.
 *
 * Extracted from health.ts to isolate the orient tool registration
 * into its own module. The orient handler is the primary entry point
 * for agents to load workspace context, governance, and active work.
 */
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, kernelCall, getWorkspaceContext, getAgentSessionId, getConversationId, setSessionOriented, isSessionOriented, runWithToolContext, type WorkspaceContext } from "../client.js";
import { trackWriteBackHintServed } from "../analytics.js";
import { success, thinWrapper, parseOrFail, type ToolResult } from "../envelope.js";
import { buildPlannedWork, hasPlannedWork, buildPlannedWorkSection, formatRecoveryBlock, type PriorSession } from "./planned-work.js";
import {
  buildTaskAlignmentLines,
  buildOperatingProtocol,
  buildCoherenceSection,
  buildDocCompletenessSection,
  formatInitiativeStatusLines,
  formatWorkstreamHealthLines,
  formatWhatNeedsAttentionLines,
  formatWhatNeedsAttentionBrief,
  formatBetConstellationLines,
  provenanceMark,
  type CoherenceSnapshot,
  type CollectionDocFields,
  type TaskAlignmentPayload,
  type InitiativeStatusItem,
  type WorkstreamHealthItem,
  type WhatNeedsAttention,
  type ConstellationEntryItem,
} from "./orient-shared.js";
import { formatTopGapPrompt, formatGapOneLiner, type ReadinessGap, type GapContext } from "../lib/gapToPrompt.js";
import { getCollectionLabel, type WorkspaceVocabCtx } from "../lib/canonicalRefs.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { handleStartPb } from "./start_pb.js";
import { handleRecordActivation } from "./record_activation.js";

/** Must match PURPOSE_GAP_PREFIX in convex/lib/constants.ts — kept in sync manually. */
const PURPOSE_GAP_PREFIX = "purpose-gap-";

type BudgetFlags = {
  advisory?: boolean;
  warning?: boolean;
  emergencyCapped?: boolean;
  truncated?: boolean;
  overflowed?: boolean;
  breachTier?: "none" | "advisory" | "warning" | "emergency";
  queryName?: string;
  attribution?: { feature?: string; route?: string };
};

type RcrtVerdict = {
  served: "ok" | "low" | "miss";
  kind: string | null;
  confidence: number;
  rubricVersion: string;
};

type DomainDetail = {
  reason?: string | null;
  nextAction?: string | null;
  evidenceGap?: string | null;
} | null;

/**
 * TEN-1740: structural mirror of convex/agentKnowledge/orientView.ts OrientStrategicContext.
 * packages/* has no documented import path into convex/, so the shape is duplicated here —
 * same pattern as MCPOrientView below. Keep in sync manually.
 */
type StrategicContext = {
  vision: string | null;
  purpose: string | null;
  activeBetCount: number;
  currentWorkPackage: string | null;
  activeTensionCount: number;
  productAreas: string[];
  productAreaCount: number;
  playingField: string[];
  playingFieldCount: number;
};

/**
 * TEN-1740: structural mirror of planned-work.ts's `formatRecoveryBlock` parameter shape
 * (itself a mirror of convex's OrientRecoveryBlock). Declared once here so every call site
 * shares one type instead of a per-call-site `as Parameters<...>` cast.
 */
type RecoveryBlockData = {
  sessionId: string;
  status: "timed_out" | "completed";
  date: string;
  duration: string;
  created: { entryId: string; collection: string; name: string }[];
  modified: { entryId: string; collection: string; name: string }[];
  draftsNeedingAttention: { entryId: string; collection: string; name: string }[];
  relationsCreated: number;
  createdOverflow?: number;
  modifiedOverflow?: number;
  draftsOverflow?: number;
};

export function formatRetrievalMissLines(verdict: RcrtVerdict, domain: DomainDetail): string[] {
  const lines: string[] = [];
  if (verdict.served !== "ok") {
    if (verdict.served === "miss") {
      lines.push(`⚠ RETRIEVAL MISS — ${verdict.kind ?? "unknown"}`);
    } else {
      lines.push(`· weak serve (provisional, ${verdict.rubricVersion}): ${verdict.kind ?? "thin"}`);
    }
  }
  if (domain?.reason) lines.push(`  Reason: ${domain.reason}`);
  if (domain?.nextAction) lines.push(`  Next action: ${domain.nextAction}`);
  if (domain?.evidenceGap) lines.push(`  Evidence gap: ${domain.evidenceGap}`);
  return lines;
}

function domainDetailFromRetrieval(retrieval: Record<string, unknown> | undefined): DomainDetail {
  if (!retrieval) return null;
  const reason = typeof retrieval.fallbackReason === "string" ? retrieval.fallbackReason : null;
  const nextAction = typeof retrieval.fallbackAction === "string" ? retrieval.fallbackAction : null;
  const evidenceGap = typeof retrieval.evidenceGap === "string" ? retrieval.evidenceGap : null;
  return reason || nextAction || evidenceGap ? { reason, nextAction, evidenceGap } : null;
}

// WP-480 S3 (FEAT-1360): the taskIsMeaningful predicate mirror is DELETED — the
// server resolves task-meaningfulness + effective tier and serves them on the
// orient payload (`taskMeaningful`, `resolvedTier`); this connector consumes them.

/**
 * WP-376 α.3: measure assembled MCP tool output bytes for orient and report
 * fire-and-forget to the gateway. The number captured here is the byte size
 * of the full MCP `tool_result` payload (content array JSON-serialized) — the
 * surface-side counterpart to the gateway's envelope-bytes measurement.
 *
 * Errors are swallowed: observability must never block the orient response or
 * affect the user-visible output. The gateway resolves workspaceId / apiKeyId
 * from the API-key context (BR-18 + injectSessionAuth).
 *
 * Skipped when `truncated` cannot be determined AND no orient view returned
 * (e.g. the early `Already oriented` return path with no orient data).
 *
 * DEC-NEW: also reports the resolved tier + task-presence for observability.
 */
function reportOrientWrapperBytes(
  toolResult: { content: Array<{ type: string; text: string }>; structuredContent?: unknown },
  truncated: boolean,
  tier: "summary" | "standard" | "full",
  taskProvided: boolean,
): void {
  try {
    const fullToolOutput = JSON.stringify({ content: toolResult.content ?? [] });
    const bytes = Buffer.byteLength(fullToolOutput, "utf8");
    // Thread the active session context (Codex re-review) so the server's N-per-key ambiguity guard
    // doesn't drop this rendered-byte report. getConversationId() is HTTP-safe (see client.ts).
    const sessionId = getAgentSessionId();
    const conversationId = getConversationId();
    void kernelMutation("agent.reportOrientMetric", {
      bytes,
      truncated,
      tier,
      taskProvided,
      ...(sessionId ? { sessionId } : {}),
      ...(conversationId ? { conversationId } : {}),
    }).catch(() => {
      // Best-effort observability — silently swallow.
    });
  } catch {
    // Even building the byte count failed (e.g. circular structuredContent in
    // an unexpected shape) — never let it surface to the caller.
  }
}

/**
 * Mark session oriented in Convex. If persistence fails with an optional coherence snapshot
 * (schema drift / validation), retry without it so orientation can still complete.
 */
async function markOrientedWithSnapshotFallback(
  agentSessionId: string,
  coherenceSnapshot?: CoherenceSnapshot,
): Promise<boolean> {
  try {
    await kernelCall("agent.markOriented", {
      sessionId: agentSessionId,
      ...(coherenceSnapshot ? { coherenceSnapshot } : {}),
    });
    setSessionOriented(true);
    return true;
  } catch {
    if (!coherenceSnapshot) return false;
    try {
      await kernelCall("agent.markOriented", { sessionId: agentSessionId });
      setSessionOriented(true);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Extract entry IDs from prior sessions for contextual priming.
 * - all: deduplicated from all sessions, capped at 10 (for scoring)
 * - lastSessionOnly: from most recent session only, capped at 5 (for temporal thread)
 */
function extractSessionEntryIds(priorSessions: PriorSession[]): {
  all: string[];
  lastSessionOnly: string[];
} {
  const allSeen = new Set<string>();
  const all: string[] = [];
  let lastSessionOnly: string[] = [];
  for (let i = 0; i < priorSessions.length; i++) {
    const s = priorSessions[i];
    const created = Array.isArray(s.entriesCreated) ? s.entriesCreated : [];
    const modified = Array.isArray(s.entriesModified) ? s.entriesModified : [];
    const ids = [...created, ...modified].filter((id): id is string => typeof id === "string" && id.length > 0);
    if (i === 0) {
      lastSessionOnly = [...new Set(ids)].slice(0, 5);
    }
    for (const id of ids) {
      if (!allSeen.has(id)) {
        allSeen.add(id);
        all.push(id);
        if (all.length >= 10) break;
      }
    }
    if (all.length >= 10) break;
  }
  return { all, lastSessionOnly };
}

// WP-484 S2 (§3): `orient` absorbs `start_pb` → action=start and
// `record_activation` → action=record-activation. `orient`'s own prior
// (action-less) behavior becomes action=task — the flat registered layer
// (§3a) below carries the superset of every absorbed action's params; the
// handler-layer discriminatedUnion (§3b, ORIENT_ACTION_UNION) enforces the
// per-action shape. `action` defaults to 'task' — `orient`'s name is REUSED
// (not "alongside"-eligible; only one tool can be named "orient"), and its
// pre-S2 behavior took no `action` param at all, so a default preserves every
// existing bare-call site (skills, agent configs) until the DEC-283 sweep (S3).
const ORIENT_ACTIONS = ["start", "task", "record-activation"] as const;
type OrientAction = (typeof ORIENT_ACTIONS)[number];

export const orientSchema = z.object({
  action: z.enum(ORIENT_ACTIONS).optional().default("task").describe(
    "'start': universal session opener (absorbs start_pb) — stage-aware setup skill or standup briefing. " +
      "'task': task-grounded context loader — the original orient behavior. " +
      "'record-activation': chat-only activation receipt writer (absorbs record_activation).",
  ),
  mode: z.enum(["full", "brief"]).optional().default("full").describe("For 'task': full = full context (default). brief = compact summary for mid-session re-orientation. Prefer using the `tier` param for depth control."),
  tier: z.enum(["summary", "standard", "full"]).optional().describe(
    "For 'task': payload depth. Defaults to summary (~10 KB) when task is provided; standard (~256 KB) when task is absent. Pass summary, standard, or full to override.",
  ),
  task: z.string().max(2000).optional().describe(
    "For 'task': natural-language task description for task-scoped context. For 'start': what you're about to work on. When provided to 'task', orient returns scored, relevant entries for the task.",
  ),
  scope: z.string().max(200).optional().describe("For 'task': optional domain scope to filter governance to entries relevant for this domain. Forwarded to Convex for workspace-specific validation."),
  // WP-486 Slice 1 (FEAT-1371, TEN-2724): the startup-signal envelope `resolveStartupDomain`
  // already consumes (`convex/agentKnowledge/startupResolver.ts:344-379`) but this tool never
  // sent. Nested to match the server's `StartupResolutionSignals` shape exactly (not a flat
  // field set) — the caller (the agent, which may have its own git/session context from prior
  // tool calls) supplies whichever fields it knows; every field is optional and independently
  // sanitized server-side (`convex/agentKnowledge/startupResolutionSignals.ts`).
  startupSignals: z.object({
    changedPaths: z.array(z.string()).max(25).optional().describe("Paths changed in the current working tree, if known (e.g. from a prior git status/diff tool call)."),
    reviewedArtifactRefs: z.array(z.string()).max(25).optional().describe("Chain entry IDs the caller has already reviewed this session, if tracked."),
    branchName: z.string().max(120).optional().describe("Current git branch name, if known."),
    worktreeName: z.string().max(120).optional().describe("Current worktree/directory name, if known."),
  }).optional().describe("For 'task': best-effort startup signals for domain resolution — changedPaths/reviewedArtifactRefs/branchName/worktreeName. All optional; omit fields you don't know."),
  invocationPath: z.enum(['session-start', 'session-close', 'manual-orient', 'handshake', 'unknown']).optional().describe("For 'task': how this orient was invoked. Defaults to 'manual-orient' (the direct `orient task=...` call shape) when omitted."),
  confirmedEntryCount: z.number().int().min(0).optional().describe(
    "For 'record-activation': confirmed-entry count from the Phase 4 capture loop. The mutation enforces >=10.",
  ),
  entriesAcrossCollections: z.number().int().min(0).optional().describe(
    "For 'record-activation': number of distinct collections those entries span. The mutation enforces >=2 (diversity soft-gate).",
  ),
  retrievalDemoConfirmed: z.boolean().optional().describe(
    "For 'record-activation': true if the retrieval round-trip ran successfully in Phase 4. The mutation rejects false.",
  ),
});

// §3b — handler layer: per-action discriminatedUnion.
const orientStartVariant = z.object({
  action: z.literal("start"),
  task: z.string().max(2000).optional(),
});
const orientTaskVariant = z.object({
  action: z.literal("task"),
  mode: z.enum(["full", "brief"]).optional().default("full"),
  tier: z.enum(["summary", "standard", "full"]).optional(),
  task: z.string().max(2000).optional(),
  scope: z.string().max(200).optional(),
  // WP-486 Slice 1 (FEAT-1371) — mirrors orientSchema's top-level declaration above; see its
  // doc comment for the shape rationale.
  startupSignals: z.object({
    changedPaths: z.array(z.string()).max(25).optional(),
    reviewedArtifactRefs: z.array(z.string()).max(25).optional(),
    branchName: z.string().max(120).optional(),
    worktreeName: z.string().max(120).optional(),
  }).optional(),
  invocationPath: z.enum(['session-start', 'session-close', 'manual-orient', 'handshake', 'unknown']).optional(),
});
const orientRecordActivationVariant = z.object({
  action: z.literal("record-activation"),
  confirmedEntryCount: z.number().int().min(0),
  entriesAcrossCollections: z.number().int().min(0),
  retrievalDemoConfirmed: z.boolean(),
});

export const orientActionUnion = z.discriminatedUnion("action", [
  orientStartVariant,
  orientTaskVariant,
  orientRecordActivationVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const ORIENT_ACTION_SPECS: Record<OrientAction, ActionParamSpec> = {
  start: { params: ["task"], description: "Universal session opener; task is optional." },
  task: { params: ["mode", "tier", "task", "scope"], description: "Task-grounded context loader; all params optional." },
  "record-activation": {
    params: ["confirmedEntryCount", "entriesAcrossCollections", "retrievalDemoConfirmed"],
    description: "All three fields are required.",
  },
};

function taskGroundingWarningLines(task?: string, hasTaskGrounding = false): string[] {
  if (hasTaskGrounding) return [];
  if (task) {
    return [
      "## Task grounding incomplete",
      "_A task was provided, but task-scoped governance/context did not load. Write tools stay locked until the task produces grounded context._",
      "",
      'Refine the task and rerun `orient task="describe the work"` or use `context action="gather"` for deeper task context.',
      "",
    ];
  }
  return [
    "## Task grounding required",
    "_This is workspace orientation only. Do not start implementation, review, diagnosis, auth, security, or governance-sensitive work until you run task-shaped orient._",
    "",
    'Run `orient task="describe the work"` to load binding governance and supporting context for the actual task.',
    "",
  ];
}

export function registerOrientTool(server: McpServer) {
  // §3b handler map — dispatched by dispatchDiscriminated via orientActionUnion.
  const orientHandlers: Record<OrientAction, (data: any) => Promise<ToolResult>> = {
    start: (data) => handleStartPb({ task: data.task }),
    task: (data) => _handleOrient({
      mode: data.mode,
      tier: data.tier,
      task: data.task,
      scope: data.scope,
      startupSignals: data.startupSignals,
      invocationPath: data.invocationPath,
    }),
    "record-activation": (data) => handleRecordActivation({
      confirmedEntryCount: data.confirmedEntryCount,
      entriesAcrossCollections: data.entriesAcrossCollections,
      retrievalDemoConfirmed: data.retrievalDemoConfirmed,
    }),
  };

  server.registerTool(
    "orient",
    {
      title: "Orient",
      description:
        "Workspace orientation and session bootstrapping. Three actions:\n\n" +
        "- **start**: Universal opener (absorbs `start_pb`) — say 'Start PB' to begin every session. " +
        "Stage-aware: fresh workspaces (blank/seeded) get the SKILL-pb-setup body verbatim; active workspaces " +
        "(grounded/connected) get a standup briefing. Orients the CURRENT session — it does not create one; " +
        "for writes, call `session action=start` first.\n" +
        "- **task**: Task-grounded context loader (the original `orient` behavior). Use after `action=start` " +
        "to load governance and context for a specific task. Completing orientation unlocks write tools.\n" +
        "- **record-activation**: Chat-only activation receipt writer (absorbs `record_activation`) — closes " +
        "the install-to-activation arc for chat-only surfaces.\n\n" +
        "**tier** (action=task): Controls payload depth.\n" +
        "- `summary` (~10 KB) — compact, calibrated for task-scoped retrieval.\n" +
        "- `standard` (~256 KB) — rich, suited to workspace standups.\n" +
        "- `full` — complete payload, no cap (use with deliberation).\n\n" +
        "Defaults: `summary` when `task` is provided; `standard` when `task` is absent. Explicit `tier` always wins.\n\n" +
        "**mode** (action=task): `full` (default) returns full context. `brief` returns compact summary — mapped to tier=summary internally.\n\n" +
        "**scope** (action=task): Optional domain scope filtering governance to entries relevant for the specified domain.",
      inputSchema: orientSchema,
      // Mixed read/write noun (§3 R3): 'start'/'record-activation' write session state.
      annotations: { readOnlyHint: false, idempotentHint: true, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(orientSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "orient", action }, () =>
        dispatchDiscriminated("orient", orientActionUnion, parsed.data, ORIENT_ACTION_SPECS, orientHandlers),
      );
    }),
  );
}

/**
 * DEC-NEW: exported orient request handler — the testable target for the
 * surface-forwarding tests (AC #5). Body is unchanged from the prior inline
 * thinWrapper handler; only the wrapping was lifted to a named export.
 */
export async function _handleOrient({ mode = "full", tier, task, scope, startupSignals, invocationPath }: {
  mode?: "full" | "brief";
  tier?: "summary" | "standard" | "full";
  task?: string;
  scope?: string;
  /** WP-486 Slice 1 (FEAT-1371) — see orientSchema.startupSignals doc comment. */
  startupSignals?: {
    changedPaths?: string[];
    reviewedArtifactRefs?: string[];
    branchName?: string;
    worktreeName?: string;
  };
  /** WP-486 Slice 1 (FEAT-1371) — defaults to 'manual-orient' below, mirroring the CLI's
   *  `pb orient --task` default (`commands/orient.ts`). */
  invocationPath?: 'session-start' | 'session-close' | 'manual-orient' | 'handshake' | 'unknown';
}) {
      const errors: string[] = [];
      // DEC-NEW + STD-219 §5 chain-version: surface forwards tier by key omission unless
      // the caller passed `tier` explicitly or `mode === 'brief'` (per-mode short-circuit).
      // When callTier is undefined the kernel applies the task-conditional default.
      const callTier: 'summary' | 'standard' | 'full' | undefined =
        tier ?? (mode === 'brief' ? 'summary' : undefined);
      // WP-480 S3 (FEAT-1360): the connector no longer pre-decides the resolved
      // tier — it sends raw inputs (tier by key omission below) and consumes the
      // server's resolution off the served payload (`resolvedTier`,
      // `taskMeaningful`), assigned right after the getOrientView fetch.

      // BET-279: --scope without --task is a silent no-op — warn the caller.
      if (scope && !task) {
        errors.push("--scope requires --task to filter governance. Scope was ignored.");
      }
      const agentSessionId = getAgentSessionId();

      if (isSessionOriented() && mode === "brief" && !task) {
        return {
          content: [{
            type: "text" as const,
            text:
              "Already oriented. Session active, writes unlocked.\n\n" +
              taskGroundingWarningLines().join("\n") +
              "Use `orient mode='full'` for workspace context or `orient task='...'` before substantive work.",
          }],
          structuredContent: success(
            "Already oriented. Session active, writes unlocked.",
            {
              alreadyOriented: true,
              sessionId: agentSessionId,
              taskGroundingStatus: "workspace_only",
              taskGroundingRequired: true,
              nextStep: 'Run `orient task="describe the work"` before substantive work.',
            },
          ),
        };
      }

      let wsCtx: WorkspaceContext | null = null;
      try {
        wsCtx = await getWorkspaceContext();
      } catch (e: unknown) {
        errors.push(`Workspace: ${e instanceof Error ? e.message : String(e)}`);
      }

      let priorSessions: PriorSession[] = [];
      let recoveryBlock: RecoveryBlockData | null = null;

      // TEN-1740: inline structural type covering fields MCP reads from OrientViewModel.
      // TODO: extract to a shared package once workspace/packages/shared-types exists.
      type MCPOrientView = {
        stage: string;
        _truncated?: boolean;
        _budget?: { bytes: number; truncated: boolean; truncationReasons: string[]; overflowed: boolean };
        /** WP-465 slice ③ (§4.1): server-computed pre-build verdict — top-level, budget-exempt. */
        taskAlignment?: TaskAlignmentPayload;
        retrievalMiss?: RcrtVerdict;
        /** WP-480 S3 (FEAT-1360): server-resolved task-meaningfulness — optional for old deploys. */
        taskMeaningful?: boolean;
        /** WP-480 S3: the server's OWN tier resolution for this serve — optional for old deploys. */
        resolvedTier?: 'summary' | 'standard' | 'full';
        strategicContext?: StrategicContext;
        governance?: { principles: unknown[]; standards: unknown[]; businessRules: unknown[] };
        activeWorkPackages?: Array<{ entryId: string | null; _id?: string; name: string; origin?: string; linkedTensions?: Array<{ entryId: string | null; name: string; severity?: string; priority?: string }> }>; tensions?: unknown[]; architectureNotes?: unknown[]; proposals?: unknown[]; absenceSignals?: unknown[];
        startup?: { groundingStatus: string; confidence: string; totalFound: number; seeds: unknown[]; bindingGovernance: { principles: unknown[]; standards: unknown[]; businessRules: unknown[]; byCollection?: unknown[] }; domainRetrieval: Record<string, unknown>; steeringContext: unknown[]; supportingContext: unknown[]; taskProfile: { workType: string; domain: string; keywords: string[] } };
        taskContext?: {
          seeds: Array<{ entryId: string | null; name: string; collectionSlug: string | null; provenance?: 'authoritative' | 'provisional' | 'floor'; anchoredBy?: string; why?: string; whyQuality?: 'rationale' | 'missing' | 'restated' }>;
          context: Array<{ entryId: string | null; name: string; collectionSlug: string | null; score: number; hop: number; origin?: 'human' | 'agent' | 'external'; provenance?: 'authoritative' | 'provisional' | 'floor'; anchoredBy?: string; why?: string; whyQuality?: 'rationale' | 'missing' | 'restated' }>;
          totalFound: number;
          confidence: string;
          constellationEntries?: Array<{ entryId: string | null; name: string; collectionSlug: string | null; canonicalKey: string | null }>;
          constellationBetName?: string;
          /** STD-219 §4 (DEC-1147): set when taskContext is a truncation stub, not full grounding. */
          isGroundingStub?: true;
          groundingNote?: string;
        };
        plannedWork?: { uncommittedDrafts: unknown[]; inProgressEntries: unknown[]; openTensions: unknown[] };
        priorSessions?: PriorSession[]; recoveryBlock?: RecoveryBlockData | null;
        writeBackHints?: Array<{ collectionSlug: string; hint: string }>;
        activeGoals?: unknown[]; strategyHighlights?: unknown[]; playingField?: unknown[];
        recentDecisions?: unknown[]; recentlySuperseded?: unknown[]; staleEntries?: unknown[];
        continuingFrom?: Array<{ entryId: string | null; name: string; collectionSlug: string | null; score: number; reasoning: string; canonicalKey: string | null; primer?: string }>;
        lastSessionTouched?: Array<{ entryId: string | null; name: string; collectionSlug: string | null; canonicalKey: string | null; primer?: string }>;
        initiativeStatus?: InitiativeStatusItem[]; workstreamHealth?: WorkstreamHealthItem[];
        whatNeedsAttention?: WhatNeedsAttention;
        trustMetrics?: { verified: number; unverified: number; noStatus: number; total: number; scannedCap: boolean };
        stalenessThresholdDays?: number;
      };
      let orientView: MCPOrientView | null = null;
      try {
        const orientArgs: {
          task?: string;
          scope?: string;
          tier?: 'summary' | 'standard' | 'full';
          renderMode?: 'brief' | 'json' | 'pretty';
          sessionId?: string;
          conversationId?: string;
          startupSignals?: {
            changedPaths?: string[];
            reviewedArtifactRefs?: string[];
            branchName?: string;
            worktreeName?: string;
          };
          invocationPath?: 'session-start' | 'session-close' | 'manual-orient' | 'handshake' | 'unknown';
        } = {};
        if (task) orientArgs.task = task;
        if (scope) orientArgs.scope = scope;
        // WP-486 Slice 1 (FEAT-1371, TEN-2724): thread the caller-supplied startup-signal
        // envelope, mirroring the CLI's `pb orient --task` hop (`commands/orient.ts`).
        // `startupSignals` is sent unconditionally (harmless when empty — `context.served`'s
        // presence telemetry benefits from an honest signal either way). `invocationPath` is NOT
        // unconditional (PR #369 review, Codex): an explicit caller-supplied value is always
        // forwarded, but the 'manual-orient' DEFAULT only applies to a task-shaped orient — see
        // the CLI's identical fix and its cited `kernel/telemetry/contextServed.ts` contract for
        // why defaulting a base (no-task) orient would conflate it with a real manual task orient
        // in the Axiom invocation-path/signal-presence slices.
        if (startupSignals && Object.keys(startupSignals).length > 0) orientArgs.startupSignals = startupSignals;
        if (invocationPath) {
          orientArgs.invocationPath = invocationPath;
        } else if (task) {
          orientArgs.invocationPath = 'manual-orient';
        }
        // DEC-NEW: forward tier by key omission when no explicit/brief intent.
        if (callTier) orientArgs.tier = callTier;
        // WP-479 review fix (Codex): MCP is an agent surface (source=mcp) rendering a structured
        // (json) payload — declare renderMode EXPLICITLY so the shared gateway seam instruments this
        // serve instead of leaving MCP traffic un-attributed, and thread the active session +
        // conversation so the M1 emit rows carry a real sessionId/conversationId (server validates
        // both). Before this, MCP orient serves logged null attribution.
        orientArgs.renderMode = 'json';
        if (agentSessionId) orientArgs.sessionId = agentSessionId;
        // getConversationId() is HTTP-safe (null in multi-session HTTP mode) — see client.ts.
        const orientConversationId = getConversationId();
        if (orientConversationId) orientArgs.conversationId = orientConversationId;
        orientView = await kernelQuery<MCPOrientView>("chain.getOrientView", orientArgs);
      } catch { /* non-critical */ }

      // WP-480 S3 (FEAT-1360): consume the server's resolution (getOrientView is
      // the payload owner) — null-guarded like retrievalMiss for old deploys that
      // don't serve the fields yet. Fallback: the caller's explicit tier is still
      // client-known input (not a mirrored rule); else 'standard' / false —
      // honestly degraded, never re-derived from a local predicate copy.
      const resolvedTier: 'summary' | 'standard' | 'full' =
        orientView?.resolvedTier ?? callTier ?? 'standard';
      const taskMeaningful: boolean = orientView?.taskMeaningful ?? false;

      // WP-436 S2: fetch Axis 2 vocabulary context for label resolution (PAT-230, DEC-1021).
      // Non-critical — if fetch fails, resolver falls back to canonicalKey literals.
      let vocabCtx: WorkspaceVocabCtx | undefined;
      try {
        type VocabResult = { collectionLabels?: Record<string, unknown>; collectionDefaults?: Record<string, unknown> };
        const vocab = await kernelQuery<VocabResult>("chain.getVocabulary", {});
        if (vocab?.collectionLabels || vocab?.collectionDefaults) {
          vocabCtx = {
            ...(vocab.collectionLabels ? { collectionLabels: vocab.collectionLabels as WorkspaceVocabCtx['collectionLabels'] } : {}),
            ...(vocab.collectionDefaults ? { collectionDefaults: vocab.collectionDefaults as WorkspaceVocabCtx['collectionDefaults'] } : {}),
          };
        }
      } catch { /* fail-open — vocab labels are advisory */ }

      if (orientView) {
        priorSessions = orientView.priorSessions ?? [];
        recoveryBlock = orientView.recoveryBlock ?? null;
      }
      const hasTaskGrounding = Boolean(task && (orientView?.taskContext?.context?.length ?? 0) > 0);

      // WP-306 S3: Emit write_back_hint_served event (STD-155)
      if (wsCtx && hasTaskGrounding && orientView?.writeBackHints) {
        const hints = Array.isArray(orientView.writeBackHints) ? orientView.writeBackHints : [];
        if (hints.length > 0) {
          trackWriteBackHintServed(wsCtx.workspaceId, {
            hint_count: hints.length,
            has_task: !!task,
          });
        }
      }

      let openTensions: any[] = [];
      try {
        const tensions = await kernelQuery<any[]>("chain.listEntries", { collectionSlug: "tensions" });
        openTensions = (tensions ?? []).filter((e: { workflowStatus?: string }) => e.workflowStatus === "open");
      } catch { /* non-critical */ }

      let allCollections: CollectionDocFields[] = [];
      try {
        allCollections = (await kernelQuery<CollectionDocFields[]>("chain.listCollections")) ?? [];
      } catch { /* non-critical — doc-completeness section is optional */ }

      let readiness: any = null;
      try {
        readiness = await kernelQuery<any>("chain.workspaceReadiness");
      } catch (e: unknown) {
        errors.push(`Readiness: ${e instanceof Error ? e.message : String(e)}`);
      }

      // BET-76 safety net: if workspace is blank, redirect to the start tool's
      // project scan flow. orient is kept for backward compat but start is the
      // correct entry point for fresh workspaces.
      if (readiness?.stage === "blank") {
        const scanLines = [
          `# Welcome to ${wsCtx?.workspaceName ?? "your workspace"}`,
          "",
          "Your workspace is fresh — let's set it up.",
          "",
          "**Recommended:** call the `start` tool instead of `orient` for the best first-run experience.",
          "It will guide you through setting up your workspace — you can tell me about your product, scan your codebase, or use a preset.",
          "",
          "Or tell me about your product and I'll start capturing knowledge directly.",
        ];

        // Blank workspaces still route into guided setup; mark oriented so start can proceed smoothly.
        let oriented = false;
        let orientationStatus: "complete" | "failed" | "no_session" = "no_session";
        if (agentSessionId) {
          try {
            await kernelCall("agent.markOriented", { sessionId: agentSessionId });
            setSessionOriented(true);
            oriented = true;
            orientationStatus = "complete";
          } catch {
            orientationStatus = "failed";
          }
        }

        const blankResult = {
          content: [{ type: "text" as const, text: scanLines.join("\n") }],
          structuredContent: success(
            "Workspace is blank. Use `orient action=start` for guided setup.",
            { stage: "blank", readinessScore: readiness?.score ?? 0, oriented, orientationStatus, redirectHint: "Use `orient action=start` for the full guided setup experience." },
            [{ tool: "orient", description: "Guided workspace setup", parameters: { action: "start" } }],
          ),
        };
        // WP-376 α.3: blank workspaces don't run the truncatable orient view; treat as not truncated.
        reportOrientWrapperBytes(blankResult, false, resolvedTier, taskMeaningful);
        return blankResult;
      }

      const lines: string[] = [];
      const isLowReadiness = readiness && readiness.score < 50;

      // --- Identity ---
      if (wsCtx) {
        lines.push(`# ${wsCtx.workspaceName}`);
        lines.push(`_Workspace \`${wsCtx.workspaceSlug}\` — Product Brain is healthy._`);
      } else {
        lines.push("# Workspace");
        lines.push("_Could not resolve workspace._");
      }
      lines.push("");
      lines.push(...taskGroundingWarningLines(task, hasTaskGrounding));
      const retrievalMissLines = orientView?.retrievalMiss
        ? formatRetrievalMissLines(
            orientView.retrievalMiss,
            domainDetailFromRetrieval(orientView.startup?.domainRetrieval),
          )
        : [];
      if (retrievalMissLines.length > 0) {
        lines.push(...retrievalMissLines);
        lines.push("");
      }

      // --- Brief mode: compact output only ---
      if (mode === "brief") {
        const briefStage = readiness?.stage ?? (readiness?.score != null ? (readiness.score < 50 ? "seeded" : "grounded") : "unknown");
        lines.push(`Brain stage: ${briefStage}`);
        if (orientView?.strategicContext) {
          const sc = orientView.strategicContext;
          if (sc.vision) lines.push(`Vision: ${sc.vision}`);
          if (sc.purpose) lines.push(`Purpose: ${sc.purpose}`);
          if (sc.productAreaCount != null && sc.productAreaCount > 0) {
            lines.push(`Product areas (${sc.productAreaCount}): ${(sc.productAreas ?? []).join(", ")}`);
          }
          if (sc.playingFieldCount != null && sc.playingFieldCount > 0) {
            lines.push(`Playing field (${sc.playingFieldCount}): ${(sc.playingField ?? []).join(", ")}`);
          }
          // WP-436 S2: resolve via vocab resolver (PAT-230, DEC-1021 Axis 2).
          const wpPlural = getCollectionLabel('work_package', 'plural', vocabCtx);
          lines.push(`${sc.activeBetCount} active ${wpPlural}, ${sc.activeTensionCount} tension(s).`);
        }
        if (orientView?.taskContext && orientView.taskContext.context.length > 0) {
          lines.push(`Task context: ${orientView.taskContext.totalFound} relevant entries (${orientView.taskContext.confidence} confidence).`);
          // STD-219 §4 (DEC-1147): announce a grounding stub even in brief mode.
          if (orientView.taskContext.isGroundingStub && orientView.taskContext.groundingNote) {
            lines.push(`⚠️ ${orientView.taskContext.groundingNote}`);
          }
        }
        // WP-465 slice ③ (§4.1): the server verdict is served in brief mode too —
        // one compact line; full librarian detail rides structuredContent.taskAlignment.
        if (orientView?.taskAlignment) {
          lines.push(`Task alignment: ${orientView.taskAlignment.verdict} — ${orientView.taskAlignment.wording.replace(/\*\*/g, "")}`);
        }
        if (orientView?.startup?.domainRetrieval) {
          const retrieval = orientView.startup.domainRetrieval;
          const scopedTo = retrieval.resolvedDomainSlug ? ` (${retrieval.resolvedDomainSlug})` : "";
          lines.push(`Domain retrieval: ${retrieval.state}${scopedTo}`);
          if (retrieval.activeSource) lines.push(`Active source: ${retrieval.activeSource}`);
          if (retrieval.directRatifiedCount != null || retrieval.inheritedRatifiedCount != null || retrieval.orgFallbackCount != null) {
            lines.push(`Domain relation evidence: ${retrieval.directRatifiedCount ?? 0} direct, ${retrieval.inheritedRatifiedCount ?? 0} inherited, ${retrieval.orgFallbackCount ?? 0} org fallback`);
          }
        }
        if (orientView?.writeBackHints && orientView.writeBackHints.length > 0) {
          lines.push("");
          lines.push("Write-back hints (what to capture this session):");
          for (const h of orientView.writeBackHints) {
            lines.push(`  ${h.collectionSlug}: ${h.hint}`);
          }
        }
        const briefActiveWps = orientView?.activeWorkPackages ?? [];
        if (briefActiveWps.length > 0) {
          for (const e of briefActiveWps) {
            const tensions = e.linkedTensions;
            const tensionPart = tensions?.length
              ? ` — ${tensions.map((t) => `${t.entryId ?? t.name} (${t.severity ?? "—"})`).join(", ")}`
              : "";
            const originPart = e.origin ? ` (origin: ${e.origin})` : "";
            lines.push(`- \`${e.entryId ?? e._id}\` ${e.name}${originPart}${tensionPart}`);
          }
        }
        // BET-240 S5: trust metrics in brief mode
        if (orientView?.trustMetrics) {
          const tm = orientView.trustMetrics;
          if (tm.unverified > 0 || tm.verified > 0) {
            const capNote = tm.scannedCap ? "+" : "";
            lines.push(`Trust: ${tm.verified} verified, ${tm.unverified} unverified, ${tm.noStatus} no-status (${tm.total}${capNote} scanned).`);
          }
        }
        // BET-126: compact What needs attention in brief mode
        const briefWna = formatWhatNeedsAttentionBrief(orientView?.whatNeedsAttention);
        if (briefWna.length > 0) {
          lines.push("");
          lines.push(...briefWna);
        }
        // FEAT-62: surface top gap in brief mode
        const briefGaps: ReadinessGap[] = readiness?.gaps ?? [];
        if (briefGaps.length > 0) {
          const oneLiner = formatGapOneLiner(briefGaps[0]);
          if (oneLiner) lines.push(oneLiner);
        }
        const briefClassificationGaps = briefGaps.filter((g) => g.id?.startsWith?.(PURPOSE_GAP_PREFIX));
        if (briefClassificationGaps.length > 0) {
          lines.push(`${briefClassificationGaps.length} collection(s) with weak purpose — classification may be inaccurate.`);
        }
        if (recoveryBlock) {
          lines.push("");
          lines.push(...formatRecoveryBlock(recoveryBlock));
        } else if (priorSessions.length > 0) {
          const last = priorSessions[0];
          const date = last.startedAt ? new Date(last.startedAt).toISOString().split("T")[0] : "unknown";
          const created = Array.isArray(last.entriesCreated) ? last.entriesCreated.length : last.entriesCreated ?? 0;
          const modified = Array.isArray(last.entriesModified) ? last.entriesModified.length : last.entriesModified ?? 0;
          lines.push(`Last session (${date}): ${created} created, ${modified} modified`);
        }
        let coherenceSnapshot: CoherenceSnapshot | undefined;
        const coherence = buildCoherenceSection();
        if (coherence) {
          lines.push("");
          lines.push(...coherence.lines);
          coherenceSnapshot = coherence.snapshot;
        }

        if (allCollections.length > 0) {
          const docCompleteness = buildDocCompletenessSection(allCollections);
          if (docCompleteness.errorCount > 0 || docCompleteness.warningCount > 0) {
            lines.push(...docCompleteness.lines);
          }
        }

        if (orientView) {
          lines.push("");
          if (task) {
            const mapGovernanceEntry = (e: any) => ({
              entryId: e.entryId,
              name: e.name,
              description: typeof e.preview === "string" ? e.preview : undefined,
              tier: typeof e.tier === "number" ? e.tier : undefined,
              // Phase 4 (DEC-1141): thread provenance fields to buildOperatingProtocol.
              provenance: e.provenance as 'authoritative' | 'provisional' | 'floor' | undefined,
              anchoredBy: typeof e.anchoredBy === "string" ? e.anchoredBy : undefined,
            });
            const gov = orientView.governance ?? { principles: [], standards: [], businessRules: [] };
            lines.push(...buildOperatingProtocol({
              principles: (gov.principles ?? []).map(mapGovernanceEntry),
              standards: (gov.standards ?? []).map(mapGovernanceEntry),
              businessRules: (gov.businessRules ?? []).map(mapGovernanceEntry),
            }, task));
          } else {
            lines.push(...buildOperatingProtocol());
          }
        }

        let orientationStatus: "complete" | "task_required" | "failed" | "no_session" = "no_session";
        if (agentSessionId && hasTaskGrounding) {
          const orientedOk = await markOrientedWithSnapshotFallback(agentSessionId, coherenceSnapshot);
          if (orientedOk) {
            orientationStatus = "complete";
            lines.push("---");
            lines.push(`Orientation complete. Session ${agentSessionId}. Write tools available.`);
          } else {
            orientationStatus = "failed";
            lines.push("---");
            lines.push("_Warning: Could not mark session as oriented. Write tools may be restricted._");
          }
        } else if (agentSessionId) {
          orientationStatus = "task_required";
          lines.push("---");
          lines.push('Workspace orientation loaded. Write tools stay locked until you run `orient task="describe the work"`.');
        } else {
          lines.push("---");
          lines.push("_No active agent session. Call `session action=start` to begin._");
        }
        // BET-β S3b: prefer _budget.truncated; fall back to _truncated for backward compat.
        const briefTruncated = orientView?._budget?.truncated ?? orientView?._truncated ?? false;
        if (briefTruncated) {
          const reasons = orientView?._budget?.truncationReasons;
          lines.push("");
          if (reasons && reasons.length > 0) {
            lines.push(`_Context truncated to fit tier budget: ${reasons.join(', ')}. Use tier=full for complete payload._`);
          } else {
            lines.push("_Context truncated to fit tier budget. Use tier=full for complete payload._");
          }
        }
        if (errors.length > 0) {
          lines.push("");
          for (const err of errors) lines.push(`- ${err}`);
        }
        const briefResult = {
          content: [{ type: "text" as const, text: lines.join("\n") }],
          structuredContent: success(
            `Oriented (brief). Stage: ${readiness?.stage ?? "unknown"}.`,
            {
              mode: "brief",
              stage: readiness?.stage ?? "unknown",
              oriented: hasTaskGrounding ? isSessionOriented() : false,
              sessionId: agentSessionId,
              taskGroundingStatus: hasTaskGrounding ? "task_scoped" : task ? "missing_task_context" : "workspace_only",
              taskGroundingRequired: !hasTaskGrounding,
              // STD-219 §4 (DEC-1147): machine-readable stub flag for agent consumers.
              ...(orientView?.taskContext?.isGroundingStub ? { groundingStub: true } : {}),
              // WP-465 slice ③ (§4.1): served verbatim — identical on CLI and MCP.
              ...(orientView?.taskAlignment ? { taskAlignment: orientView.taskAlignment } : {}),
              // RCRT miss-alarm: machine-readable verdict for structuredContent
              // consumers (parity with the rendered prose alarm).
              ...(orientView?.retrievalMiss ? { retrievalMiss: orientView.retrievalMiss } : {}),
              orientationStatus,
              nextStep: hasTaskGrounding ? undefined : 'Run `orient task="describe the work"` before substantive work.',
              ...(orientView?._budget ? { _budget: orientView._budget } : {}),
            },
          ),
        };
        // WP-376 α.3: surface-side bytes — full assembled MCP tool output.
        reportOrientWrapperBytes(briefResult, briefTruncated, resolvedTier, taskMeaningful);
        return briefResult;
      }

      const orientStage: string = readiness?.stage ?? "seeded";

      // --- Full mode: New vs neglected framing ---
      if (isLowReadiness && wsCtx?.createdAt) {
        const ageDays = Math.floor((Date.now() - wsCtx.createdAt) / (1000 * 60 * 60 * 24));
        if (ageDays >= 30) {
          lines.push(`Your workspace has been around for ${ageDays} days and is still at the **${orientStage}** stage.`);
          lines.push("Let's close the gaps — or if the current structure doesn't fit, we can reshape it.");
          lines.push("");
        }
      }
      // --- Single next action for low-readiness (FEAT-62: conversational gap prompts) ---
      let fullCoherenceSnapshot: CoherenceSnapshot | undefined;
      if (isLowReadiness) {
        const captureBehaviorNote =
          (readiness?.governanceMode ?? wsCtx?.governanceMode ?? "open") === "open"
            ? "_In Open mode, captures are accepted automatically unless you ask me to keep them as drafts._"
            : "_Everything stays as a draft until you confirm._";

        const gaps: ReadinessGap[] = readiness.gaps ?? [];
        if (gaps.length > 0) {
          const gapCtx: GapContext = {
            stage: orientStage,
            passedChecks: readiness.passedChecks ?? 0,
            totalChecks: readiness.totalChecks ?? 0,
            score: readiness.score ?? 0,
          };

          lines.push(formatTopGapPrompt(gaps[0], gaps.length - 1, gapCtx));
          lines.push("");
          lines.push(captureBehaviorNote);
          lines.push("");

          const remainingGaps = gaps.length - 1;
          if (remainingGaps > 0 || openTensions.length > 0) {
            const parts: string[] = [];
            if (remainingGaps > 0) parts.push(`${remainingGaps} more area${remainingGaps === 1 ? "" : "s"} to cover`);
            if (openTensions.length > 0) parts.push(`${openTensions.length} open tension${openTensions.length === 1 ? "" : "s"}`);
            lines.push(`_${parts.join(" and ")} — ask for full status to see everything._`);
            lines.push("");
          }
        }

        lines.push("_Need a collection that doesn't exist yet? Just ask — I'll set it up._");
        lines.push("");
      } else if (readiness) {
        const fmt = (e: any) => {
          const type = e.canonicalKey ?? 'generic';
          const stratum = e.stratum ?? '?';
          // BET-222 E3: show confidence + review overdue for decisions
          const confidencePart = e.confidence ? ` [confidence: ${e.confidence}]` : '';
          const reviewOverdue = e.reviewAt && new Date(e.reviewAt).getTime() < Date.now();
          const overduePart = reviewOverdue ? ' [review overdue]' : '';
          // BET-240 S0: surface origin for trust visibility
          const originPart = e.origin ? ` (origin: ${e.origin})` : '';
          // Phase 4 (DEC-1141): prefix provenance marker and append anchoredBy compactly.
          const mark = provenanceMark(e.provenance);
          const anchorSuffix = e.anchoredBy ? ` (${e.anchoredBy})` : '';
          return `- ${mark}\`${e.entryId ?? e._id}\` [${type} · ${stratum}] ${e.name}${anchorSuffix}${originPart}${confidencePart}${overduePart}`;
        };

        // BET-99 / FEAT-182: Codebase coherence check (runs once, shared across branches)
        const fullCoherence = buildCoherenceSection();
        if (fullCoherence) {
          fullCoherenceSnapshot = fullCoherence.snapshot;
        }

        if (task) {
          // ── FULL LAYER: task provided → rich, task-scoped context ──────────
          lines.push(`**Brain stage: ${orientStage}.** Working on: **${task}**`);
          lines.push("");

          if (orientView?.strategicContext) {
            const sc = orientView.strategicContext;
            lines.push("## Strategic Context");
            if (sc.vision) lines.push(`**Vision:** ${sc.vision}`);
            if (sc.purpose) lines.push(`**Purpose:** ${sc.purpose}`);
            if (sc.productAreaCount != null && sc.productAreaCount > 0) {
              lines.push(`**Product areas (${sc.productAreaCount}):** ${(sc.productAreas ?? []).join(", ")}`);
            }
            if (sc.playingFieldCount != null && sc.playingFieldCount > 0) {
              lines.push(`**Playing field (${sc.playingFieldCount}):** ${(sc.playingField ?? []).join(", ")}`);
            }
            // WP-436 S4: currentWorkPackage is the canonical field (legacy currentBet dropped).
            const currentWP = sc.currentWorkPackage;
            const wpSingularFull = getCollectionLabel('work_package', 'singular', vocabCtx);
            const wpPluralFull = getCollectionLabel('work_package', 'plural', vocabCtx);
            const betLine = currentWP
              ? `**Current ${wpSingularFull}:** ${currentWP}. ${sc.activeBetCount} active ${wpPluralFull}.`
              : `No active ${wpPluralFull}.`;
            lines.push(`${betLine} ${sc.activeTensionCount} open tension(s).`);
            lines.push("");
          }

          if (orientView?.continuingFrom && orientView.continuingFrom.length > 0) {
            lines.push("## Continuing from");
            lines.push("_Prior-session entries most relevant to your task._");
            lines.push("");
            for (const e of orientView.continuingFrom) {
              const id = e.entryId ?? e.name;
              const type = e.canonicalKey ?? "generic";
              const coll = e.collectionSlug ? ` [${e.collectionSlug}]` : "";
              lines.push(`- \`${id}\` (score ${e.score}) [${type}]${coll} — ${e.name}`);
              if (e.reasoning) lines.push(`  _${e.reasoning}_`);
              if ("primer" in e && typeof e.primer === "string" && e.primer.length > 0) {
                const PRIMER_MAX = 1200;
                const primerText = e.primer.length > PRIMER_MAX
                  ? e.primer.slice(0, PRIMER_MAX) + "\n… (truncated — use entries action=get for full primer)"
                  : e.primer;
                lines.push("");
                lines.push("  **Primer (paste when starting implementation):**");
                lines.push("  ```");
                for (const line of primerText.split("\n")) lines.push("  " + line);
                lines.push("  ```");
                lines.push("");
              }
            }
            lines.push("");
          }

          if (orientView?.lastSessionTouched && orientView.lastSessionTouched.length > 0) {
            lines.push("## Last session touched");
            lines.push("_Entries created or modified in your most recent session._");
            lines.push("");
            for (const e of orientView.lastSessionTouched) {
              const id = e.entryId ?? e.name;
              const type = e.canonicalKey ?? "generic";
              const coll = e.collectionSlug ? ` [${e.collectionSlug}]` : "";
              lines.push(`- \`${id}\` [${type}]${coll} — ${e.name}`);
              if ("primer" in e && typeof e.primer === "string" && e.primer.length > 0) {
                const PRIMER_MAX = 1200;
                const primerText = e.primer.length > PRIMER_MAX
                  ? e.primer.slice(0, PRIMER_MAX) + "\n… (truncated — use entries action=get for full primer)"
                  : e.primer;
                lines.push("");
                lines.push("  **Primer:**");
                lines.push("  ```");
                for (const line of primerText.split("\n")) lines.push("  " + line);
                lines.push("  ```");
                lines.push("");
              }
            }
            lines.push("");
          }

          if (orientView?.taskContext && orientView.taskContext.context.length > 0) {
            const tc = orientView.taskContext;
            lines.push("## Task Context");
            // STD-219 §4 (DEC-1147): announce a grounding stub so the agent knows it is
            // operating lean and must fetch full context before consequential work.
            if (tc.isGroundingStub && tc.groundingNote) {
              lines.push(`> ⚠️ ${tc.groundingNote}`);
              lines.push("");
            }
            if (orientView.startup?.domainRetrieval) {
              const retrieval = orientView.startup.domainRetrieval;
              const scopedTo = retrieval.resolvedDomainSlug ? ` (${retrieval.resolvedDomainSlug})` : "";
              lines.push(`_Domain retrieval: ${retrieval.state}${scopedTo}_`);
              if (retrieval.activeSource) lines.push(`_Active source: ${retrieval.activeSource}_`);
              lines.push("");
            }
            lines.push(`_Task-scoped entries (${tc.confidence} confidence, ${tc.totalFound} relevant)`);
            lines.push("");
            for (const e of tc.context) {
              const id = e.entryId ?? e.name;
              const coll = e.collectionSlug ? ` [${e.collectionSlug}]` : "";
              const originTag = e.origin ? ` (origin: ${e.origin})` : "";
              lines.push(`- \`${id}\` (score ${e.score})${coll}${originTag}${e.name !== id ? ` — ${e.name}` : ""}`);
              // NOTE (typecheck audit): taskContext.context entries never carry a `preview`
              // field on the wire (see convex/agentKnowledge/orientView.ts — unlike sibling
              // sections such as activeGoals/recentDecisions). A dead `if (e.preview)` branch
              // here always evaluated to false; removed rather than kept as unreachable code.
            }
            lines.push("");
          }

          if (orientView?.taskContext?.constellationEntries && orientView.taskContext.constellationEntries.length > 0) {
            // ConstellationEntryItem requires non-null entryId/collectionSlug; the gateway can
            // legitimately serve null for either (convex/agentKnowledge/orientView.ts maps
            // `entryId ?? null` / `collectionSlug ?? null`). Fall back the same way the rest of
            // this file already does for nullable entryIds (e.g. the continuingFrom/lastSessionTouched
            // loops below use `e.entryId ?? e.name`); a null collectionSlug already fails to match
            // formatBetConstellationLines' known slug buckets ("decisions"/"tensions"/"features") and
            // renders nothing either way, so "unclassified" preserves that no-op behavior.
            const constellationEntries: ConstellationEntryItem[] = orientView.taskContext.constellationEntries.map((e) => ({
              entryId: e.entryId ?? e.name,
              name: e.name,
              collectionSlug: e.collectionSlug ?? "unclassified",
              canonicalKey: e.canonicalKey,
            }));
            lines.push(...formatBetConstellationLines(
              constellationEntries,
              orientView.taskContext.constellationBetName,
            ));
          }

          if (orientView?.writeBackHints && orientView.writeBackHints.length > 0) {
            lines.push("");
            lines.push("## Write-Back Hints");
            lines.push("_What to capture this session (derived from task context):_");
            lines.push("");
            for (const h of orientView.writeBackHints) {
              lines.push(`- **${h.collectionSlug}**: ${h.hint}`);
            }
            lines.push("");
          }

          if (orientView) {
            // WP-465 slice ③ (§4.1): render the SERVER-computed verdict — the local
            // matcher is gone; the resolver lives behind chain.getOrientView so the
            // pb CLI and MCP serve the identical taskAlignment.
            lines.push(...buildTaskAlignmentLines(orientView.taskAlignment));
            // BET-126: Initiative Status, Workstream Health, What Needs Attention (full, with task)
            lines.push(...formatInitiativeStatusLines(orientView.initiativeStatus));
            lines.push(...formatWorkstreamHealthLines(orientView.workstreamHealth));
            lines.push(...formatWhatNeedsAttentionLines(orientView.whatNeedsAttention));
            // BET-240 S5: trust metrics (full, with task)
            if (orientView.trustMetrics) {
              const tm = orientView.trustMetrics;
              if (tm.verified > 0 || tm.unverified > 0) {
                const capNote = tm.scannedCap ? " (capped at 500)" : "";
                lines.push("## Trust metrics");
                lines.push(`_Entry verification status across workspace${capNote}._`);
                lines.push("");
                lines.push(`- **Verified:** ${tm.verified}`);
                lines.push(`- **Unverified:** ${tm.unverified}`);
                lines.push(`- **No status (pre-BET-240):** ${tm.noStatus}`);
                lines.push(`- **Total scanned:** ${tm.total}`);
                lines.push("");
              }
            }
          }

          if (fullCoherence) {
            lines.push(...fullCoherence.lines);
          }

          if (allCollections.length > 0) {
            const docCompleteness = buildDocCompletenessSection(allCollections);
            if (docCompleteness.errorCount > 0 || docCompleteness.warningCount > 0) {
              lines.push(...docCompleteness.lines);
            }
          }

          if (orientView) {
            const fullActiveWps = orientView.activeWorkPackages ?? [];
            if (fullActiveWps.length > 0) {
              lines.push("## Active work packages — current scope");
              lines.push("_Work outside these work packages requires explicit user confirmation._");
              lines.push("");
              for (const e of fullActiveWps) {
                lines.push(fmt(e));
                const tensions = e.linkedTensions;
                if (tensions?.length) {
                  const tensionLines = tensions.map((t) => {
                    const meta = [t.severity, t.priority].filter(Boolean).join(", ");
                    return `\`${t.entryId ?? t.name}\` (${t.name}${meta ? `, ${meta}` : ""})`;
                  });
                  lines.push(`  Tensions: ${tensionLines.join("; ")}`);
                }
              }
              lines.push("");
            }
            const fullActiveGoals = orientView.activeGoals ?? [];
            if (fullActiveGoals.length > 0) {
              lines.push("## Active goals");
              fullActiveGoals.forEach((e) => lines.push(fmt(e)));
              lines.push("");
            }
            const fullStrategyHighlights = orientView.strategyHighlights ?? [];
            if (fullStrategyHighlights.length > 0) {
              lines.push("## Strategy highlights");
              lines.push("_One-sentence strategy, positioning, moat, business model, GTM — high-level strategic context._");
              lines.push("");
              fullStrategyHighlights.forEach((e) => lines.push(fmt(e)));
              lines.push("");
            }
            const fullPlayingField = orientView.playingField ?? [];
            if (fullPlayingField.length > 0) {
              lines.push("## Playing field");
              lines.push("_Products and opportunities in the strategic landscape._");
              lines.push("");
              fullPlayingField.forEach((e) => lines.push(fmt(e)));
              lines.push("");
            }
            const fullRecentDecisions = orientView.recentDecisions ?? [];
            if (fullRecentDecisions.length > 0) {
              lines.push("## Recent decisions");
              fullRecentDecisions.forEach((e) => lines.push(fmt(e)));
              lines.push("");
            }
            const fullRecentlySuperseded = orientView.recentlySuperseded ?? [];
            if (fullRecentlySuperseded.length > 0) {
              lines.push("## Recently superseded");
              fullRecentlySuperseded.forEach((e) => lines.push(fmt(e)));
              lines.push("");
            }
            const fullStaleEntries = orientView.staleEntries ?? [];
            if (fullStaleEntries.length > 0) {
              lines.push("## Needs confirmation");
              lines.push(`_Domain stratum entries not confirmed in ${orientView.stalenessThresholdDays} days._`);
              fullStaleEntries.forEach((e) => lines.push(fmt(e)));
              lines.push("");
            }
            const fullArchitectureNotes = orientView.architectureNotes ?? [];
            if (fullArchitectureNotes.length > 0) {
              lines.push("## Architecture notes");
              fullArchitectureNotes.forEach((e) => lines.push(fmt(e)));
              lines.push("");
            }

            const mapGovernanceEntry = (e: any) => ({
              entryId: e.entryId,
              name: e.name,
              description: typeof e.preview === "string" ? e.preview : undefined,
              tier: typeof e.tier === "number" ? e.tier : undefined,
              // Phase 4 (DEC-1141): thread provenance fields to buildOperatingProtocol.
              provenance: e.provenance as 'authoritative' | 'provisional' | 'floor' | undefined,
              anchoredBy: typeof e.anchoredBy === "string" ? e.anchoredBy : undefined,
            });
            const gov = orientView.governance ?? { principles: [], standards: [], businessRules: [] };
            lines.push(...buildOperatingProtocol({
              principles: (gov.principles ?? []).map(mapGovernanceEntry),
              standards: (gov.standards ?? []).map(mapGovernanceEntry),
              businessRules: (gov.businessRules ?? []).map(mapGovernanceEntry),
            }, task));
          }

          let allEntries: any[] = [];
          try {
            allEntries = await kernelQuery<any[]>("chain.listEntries", {
              fields: ["_id", "entryId", "name", "status", "workflowStatus", "collectionSlug", "stratum", "canonicalKey"],
            }) ?? [];
          } catch { /* non-critical */ }

          const plannedWork = buildPlannedWork(allEntries);

          if (hasPlannedWork(plannedWork)) {
            lines.push(...buildPlannedWorkSection(plannedWork, priorSessions, recoveryBlock));
          } else if (recoveryBlock) {
            lines.push(...formatRecoveryBlock(recoveryBlock));
          }
        } else {
          // ── COMPACT LAYER: no task → standup only ─────────────────────────
          // Identity + active bets + OP (compact) + session continuity + CTA.
          // Everything else loads on demand when agent states a task.
          lines.push(`**Brain stage: ${orientStage}.**`);
          lines.push("");

          if (orientView?.activeWorkPackages?.length) {
            lines.push("## Active work packages — current scope");
            lines.push("_Work outside these work packages requires explicit user confirmation._");
            lines.push("");
            for (const e of orientView.activeWorkPackages) {
              lines.push(fmt(e));
            }
            lines.push("");
          }

          // BET-126: Initiative Status, Workstream Health, What Needs Attention (full, no task)
          lines.push(...formatInitiativeStatusLines(orientView?.initiativeStatus));
          lines.push(...formatWorkstreamHealthLines(orientView?.workstreamHealth));
          lines.push(...formatWhatNeedsAttentionLines(orientView?.whatNeedsAttention));

          // BET-240 S5: trust metrics (full, no task)
          if (orientView?.trustMetrics) {
            const tm = orientView.trustMetrics;
            if (tm.verified > 0 || tm.unverified > 0) {
              const capNote = tm.scannedCap ? " (capped at 500)" : "";
              lines.push("## Trust metrics");
              lines.push(`_Entry verification status across workspace${capNote}._`);
              lines.push("");
              lines.push(`- **Verified:** ${tm.verified}`);
              lines.push(`- **Unverified:** ${tm.unverified}`);
              lines.push(`- **No status (pre-BET-240):** ${tm.noStatus}`);
              lines.push(`- **Total scanned:** ${tm.total}`);
              lines.push("");
            }
          }

          lines.push(...buildOperatingProtocol());

          if (fullCoherence) {
            lines.push(...fullCoherence.lines);
          }

          if (allCollections.length > 0) {
            const docCompleteness = buildDocCompletenessSection(allCollections);
            if (docCompleteness.errorCount > 0 || docCompleteness.warningCount > 0) {
              lines.push(...docCompleteness.lines);
            }
          }

          if (priorSessions.length > 0 && !recoveryBlock) {
            const last = priorSessions[0];
            const date = last.startedAt ? new Date(last.startedAt).toISOString().split("T")[0] : "unknown";
            const created = Array.isArray(last.entriesCreated) ? last.entriesCreated.length : last.entriesCreated ?? 0;
            const modified = Array.isArray(last.entriesModified) ? last.entriesModified.length : last.entriesModified ?? 0;
            lines.push(`_Last session (${date}): ${created} created, ${modified} modified._`);
            lines.push("");
          }

          if (recoveryBlock) {
            lines.push(...formatRecoveryBlock(recoveryBlock));
          }

          lines.push("**What are you working on?** Tell me and I'll load relevant governance and context.");
          lines.push("");
        }
      }

      // --- Classification gaps (INS-116) ---
      const classificationGaps = readiness?.gaps?.filter((g: { id?: string }) => g.id?.startsWith?.(PURPOSE_GAP_PREFIX)) ?? [];
      if (classificationGaps.length > 0) {
        lines.push("## Classification gaps");
        lines.push("_Collections with weak purpose — entry classification may be inaccurate._");
        lines.push("");
        for (const g of classificationGaps) {
          lines.push(`- ${(g as { label?: string }).label ?? g.id?.replace?.("purpose-gap-", "") ?? "unknown"}`);
        }
        lines.push("");
      }

      if (errors.length > 0) {
        lines.push("## Errors");
        for (const err of errors) lines.push(`- ${err}`);
        lines.push("");
      }

      // --- Mark session as oriented ---
      let orientationStatus: "complete" | "task_required" | "failed" | "no_session" = "no_session";
      if (agentSessionId && hasTaskGrounding) {
        const orientedOk = await markOrientedWithSnapshotFallback(agentSessionId, fullCoherenceSnapshot);
        if (orientedOk) {
          orientationStatus = "complete";

          lines.push("---");
          lines.push(`Orientation complete. Session ${agentSessionId}. Write tools available.`);
        } else {
          orientationStatus = "failed";
          lines.push("---");
          lines.push("_Warning: Could not mark session as oriented. Write tools may be restricted._");
        }

        // Record session signal: orient was called (implicit feedback for the intelligence kernel)
        try {
          await kernelMutation("chain.recordSessionSignal", {
            sessionId: agentSessionId,
            signalType: "immediate_context_load",
            metadata: { source: "orient" },
          });
        } catch (err) {
          process.stderr.write(`[MCP] recordSessionSignal failed: ${(err as Error).message}\n`);
          // Non-fatal: orient still succeeds; signal is for analytics
        }
      } else if (agentSessionId) {
        orientationStatus = "task_required";
        lines.push("---");
        lines.push('Workspace orientation loaded. Write tools stay locked until you run `orient task="describe the work"`.');
      } else {
        lines.push("---");
        lines.push("_No active agent session. Call `session action=start` to begin a tracked session._");
      }

      // BET-β S3b: prefer _budget.truncated; fall back to _truncated for backward compat.
      const fullTruncated = orientView?._budget?.truncated ?? orientView?._truncated ?? false;
      if (fullTruncated) {
        const reasons = orientView?._budget?.truncationReasons;
        lines.push("");
        if (reasons && reasons.length > 0) {
          lines.push(`_Context truncated to fit tier budget: ${reasons.join(', ')}. Use tier=full for complete payload._`);
        } else {
          lines.push("_Context truncated to fit tier budget. Use tier=full for complete payload._");
        }
      }

      const fullResult = {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: success(
          `Oriented (full). Stage: ${orientStage}. ${isLowReadiness ? "Low readiness — gaps remain." : "Ready."}`,
          {
            mode: "full",
            stage: orientStage,
            oriented: hasTaskGrounding ? isSessionOriented() : false,
            sessionId: agentSessionId,
            taskGroundingStatus: hasTaskGrounding ? "task_scoped" : task ? "missing_task_context" : "workspace_only",
            taskGroundingRequired: !hasTaskGrounding,
            // STD-219 §4 (DEC-1147): machine-readable stub flag for agent consumers.
            ...(orientView?.taskContext?.isGroundingStub ? { groundingStub: true } : {}),
            // WP-465 slice ③ (§4.1): served verbatim — identical on CLI and MCP.
            ...(orientView?.taskAlignment ? { taskAlignment: orientView.taskAlignment } : {}),
            // RCRT miss-alarm: machine-readable verdict for structuredContent
            // consumers (parity with the rendered prose alarm).
            ...(orientView?.retrievalMiss ? { retrievalMiss: orientView.retrievalMiss } : {}),
            domainRetrieval: orientView?.startup?.domainRetrieval,
            orientationStatus,
            nextStep: hasTaskGrounding ? undefined : 'Run `orient task="describe the work"` before substantive work.',
            ...(orientView?._budget ? { _budget: orientView._budget } : {}),
          },
        ),
      };
      // WP-376 α.3: surface-side bytes — full assembled MCP tool output.
      reportOrientWrapperBytes(fullResult, fullTruncated, resolvedTier, taskMeaningful);
      return fullResult;
}
